import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { anonymousClient } from 'src/utils/api-client';

const SUPPORTED_CLIENT_SLUGS = ['bos'];
type ConsentSection = {
  key: string;
  consentKeys: string[];
  dependsOnKey?: string;
  footerKey?: string;
};

const CONSENT_SECTIONS: ConsentSection[] = [
  {
    key: 'ghg_calculation',
    consentKeys: ['bos_ghg_calculation'],
  },
  {
    key: 'report_to_bank',
    consentKeys: ['bos_report_to_bank'],
  },
  {
    key: 'quantifier_contact',
    dependsOnKey: 'bos_quantifier_contact',
    consentKeys: [
      'bos_quantifier_contact',
      'bos_quantifier_contact_email',
      'bos_quantifier_contact_phone',
      'bos_quantifier_contact_sms',
    ],
    footerKey: 'bos.consents.quantifier_contact.footer',
  },
  {
    key: 'bank_contact',
    dependsOnKey: 'bos_bank_contact',
    consentKeys: [
      'bos_bank_contact',
      'bos_bank_contact_email',
      'bos_bank_contact_phone',
      'bos_bank_contact_sms',
    ],
  },
  {
    key: 'terms_of_service',
    consentKeys: ['bos_terms_of_service'],
  },
  {
    key: 'client_statement',
    consentKeys: [
      'bos_not_quantifier_client',
      'bos_not_quantifier_competitor',
      'bos_not_financial_services_competitor',
      'bos_representative_authority',
    ],
  },
];
const CONSENT_KEYS = CONSENT_SECTIONS.flatMap((section) => section.consentKeys);
const REQUIRED_ACCEPTED_CONSENT_KEYS = [
  'bos_ghg_calculation',
  'bos_report_to_bank',
  'bos_terms_of_service',
  'bos_not_quantifier_client',
  'bos_not_quantifier_competitor',
  'bos_not_financial_services_competitor',
  'bos_representative_authority',
];
const isConsentRequired = (key: string) =>
  REQUIRED_ACCEPTED_CONSENT_KEYS.includes(key);
const TERMS_OF_SERVICE_URL =
  'https://uploads-ssl.webflow.com/6447acefc459ccf396df1aa7/6512f30e2d7b0c745dd774cd_regulamin.pdf';
const PRIVACY_POLICY_URL =
  'https://s3.eu-central-1.amazonaws.com/envirly-placeholder/bos-privacy-policy.pdf';
// When replacing the documents, rename the file or add a ?v= suffix so
// browsers holding a cached copy fetch the new one.
const QUANTIFIER_INFO_CLAUSE_URL = '/docs/Klauzula-informacyjna-Quantifier.pdf';
const BOS_BANK_INFO_CLAUSE_URL = '/docs/Klauzula-informacyjna-BOS.pdf?v=2';

type FormState = {
  company_name: string;
  vat_identifier: string;
  pkd_code: string;
  email: string;
  phone_number: string;
  website: string;
  consents: Record<string, boolean>;
};

const initialConsents = CONSENT_KEYS.reduce<Record<string, boolean>>(
  (acc, key) => ({ ...acc, [key]: false }),
  {}
);
const dependentConsentKeys = CONSENT_SECTIONS.reduce<Record<string, string[]>>(
  (acc, section) =>
    section.dependsOnKey
      ? {
          ...acc,
          [section.dependsOnKey]: section.consentKeys.filter(
            (key) => key !== section.dependsOnKey
          ),
        }
      : acc,
  {}
);

const isEmailValid = (email: string) =>
  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);
const VAT_IDENTIFIER_REGEX = /^\d{10}$/;
const PKD_CODE_REGEX = /^\d{2}\.\d{2}\.[A-Z]$/;

const isVatIdentifierValid = (vatIdentifier: string) =>
  VAT_IDENTIFIER_REGEX.test(vatIdentifier);
const isPkdCodeValid = (pkdCode: string) => PKD_CODE_REGEX.test(pkdCode);

const sanitizeFieldValue = (
  field: keyof Omit<FormState, 'consents'>,
  value: string
) => {
  if (field === 'vat_identifier') {
    return value.replace(/\D/g, '').slice(0, 10);
  }
  if (field === 'pkd_code') {
    return value
      .toUpperCase()
      .replace(/[^0-9A-Z.]/g, '')
      .slice(0, 7);
  }
  if (field === 'phone_number') {
    return value.replace(/\D/g, '').slice(0, 15);
  }
  return value;
};

const renderIntro = (intro: string) =>
  intro.split(/(support@envirly\.com|www\.envirly\.pl)/g).map((part) => {
    if (part === 'support@envirly.com') {
      return (
        <Box component="a" href="mailto:support@envirly.com" key={part}>
          {part}
        </Box>
      );
    }
    if (part === 'www.envirly.pl') {
      return (
        <Box
          component="a"
          href="https://www.envirly.pl"
          target="_blank"
          rel="noreferrer"
          key={part}
        >
          {part}
        </Box>
      );
    }
    return part;
  });

const renderConsentLabel = (text: string) =>
  text
    .split(
      /(Regulaminu|Regulamin|Polityką Prywatności|polityce prywatności|Terms of Service|Terms of Use|Privacy Policy|privacy policy|klauzulą informacyjną Quantifier sp\. z o\.o\.|klauzulą informacyjną Banku Ochrony Środowiska S\.A\.|Quantifier sp\. z o\.o\. information clause|information clause of Bank Ochrony Środowiska S\.A\.)/g
    )
    .map((part, index) => {
      if (
        part === 'Regulaminu' ||
        part === 'Regulamin' ||
        part === 'Terms of Service' ||
        part === 'Terms of Use'
      ) {
        return (
          <Box
            component="a"
            href={TERMS_OF_SERVICE_URL}
            target="_blank"
            rel="noreferrer"
            key={`${part}-${index}`}
          >
            {part}
          </Box>
        );
      }
      if (
        part === 'Polityką Prywatności' ||
        part === 'polityce prywatności' ||
        part === 'Privacy Policy' ||
        part === 'privacy policy'
      ) {
        return (
          <Box
            component="a"
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noreferrer"
            key={`${part}-${index}`}
          >
            {part}
          </Box>
        );
      }
      if (
        part === 'klauzulą informacyjną Quantifier sp. z o.o.' ||
        part === 'Quantifier sp. z o.o. information clause'
      ) {
        return (
          <Box
            component="a"
            href={QUANTIFIER_INFO_CLAUSE_URL}
            target="_blank"
            rel="noreferrer"
            key={`${part}-${index}`}
          >
            {part}
          </Box>
        );
      }
      if (
        part === 'klauzulą informacyjną Banku Ochrony Środowiska S.A.' ||
        part === 'information clause of Bank Ochrony Środowiska S.A.'
      ) {
        return (
          <Box
            component="a"
            href={BOS_BANK_INFO_CLAUSE_URL}
            target="_blank"
            rel="noreferrer"
            key={`${part}-${index}`}
          >
            {part}
          </Box>
        );
      }
      return part;
    });

export default function ClientRegistration() {
  const { clientSlug = '' } = useParams();
  const { t } = useTranslation('client_registration');
  const [form, setForm] = React.useState<FormState>({
    company_name: '',
    vat_identifier: '',
    pkd_code: '',
    email: '',
    phone_number: '',
    website: '',
    consents: initialConsents,
  });
  const [submitted, setSubmitted] = React.useState(false);

  const isSupportedClient = SUPPORTED_CLIENT_SLUGS.includes(clientSlug);
  const vatIdentifier = form.vat_identifier.trim();
  const pkdCode = form.pkd_code.trim();
  const isContactSectionMissingChannel = (section: ConsentSection) =>
    Boolean(
      section.dependsOnKey &&
      form.consents[section.dependsOnKey] &&
      !section.consentKeys.some(
        (key) => key !== section.dependsOnKey && form.consents[key]
      )
    );
  const isComplete = Boolean(
    isSupportedClient &&
    form.company_name.trim() &&
    isVatIdentifierValid(vatIdentifier) &&
    isPkdCodeValid(pkdCode) &&
    isEmailValid(form.email.trim()) &&
    REQUIRED_ACCEPTED_CONSENT_KEYS.every(
      (key) => form.consents[key] === true
    ) &&
    !CONSENT_SECTIONS.some(isContactSectionMissingChannel)
  );

  const mutation = useMutation({
    mutationFn: () =>
      anonymousClient.post('/web/public/client-registration-requests', {
        client_slug: clientSlug,
        company_name: form.company_name,
        vat_identifier: vatIdentifier,
        pkd_code: pkdCode,
        email: form.email,
        phone_number: form.phone_number.trim(),
        website: form.website,
        consents: form.consents,
      }),
    onSuccess: () => setSubmitted(true),
  });

  const updateField =
    (field: keyof Omit<FormState, 'consents'>) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = sanitizeFieldValue(field, event.target.value);
      setForm((current) => ({ ...current, [field]: value }));
    };

  const updateConsent =
    (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setForm((current) => ({
        ...current,
        consents: {
          ...current.consents,
          [key]: checked,
          ...(!checked
            ? Object.fromEntries(
                (dependentConsentKeys[key] || []).map((dependentKey) => [
                  dependentKey,
                  false,
                ])
              )
            : {}),
        },
      }));
    };

  if (!isSupportedClient) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', px: 3, py: 8 }}>
        <Alert severity="error">{t('bos.unsupportedClient')}</Alert>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', px: 3, py: 8 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
          {t('bos.successTitle')}
        </Typography>
        <Typography>{t('bos.successMessage')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', px: 3, py: 8 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        {t('bos.title')}
      </Typography>
      <Typography sx={{ mb: 2, whiteSpace: 'pre-line' }}>
        {renderIntro(t('bos.intro'))}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 4, fontSize: '0.75rem' }}
      >
        {t('bos.verificationFootnote')}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Typography sx={{ mb: 3 }}>{t('bos.consentsTitle')}</Typography>

      <Box component="form" noValidate>
        <TextField
          label={`* ${t('bos.companyName')}`}
          value={form.company_name}
          onChange={updateField('company_name')}
          fullWidth
          inputProps={{ required: true }}
          sx={{ mb: 3 }}
        />
        <TextField
          label={`* ${t('bos.vatIdentifier')}`}
          value={form.vat_identifier}
          onChange={updateField('vat_identifier')}
          fullWidth
          inputProps={{
            required: true,
            inputMode: 'numeric',
            maxLength: 10,
            pattern: '\\d{10}',
          }}
          error={!!vatIdentifier && !isVatIdentifierValid(vatIdentifier)}
          helperText={
            !!vatIdentifier &&
            !isVatIdentifierValid(vatIdentifier) &&
            t('bos.vatIdentifierError')
          }
          sx={{ mb: 3 }}
        />
        <TextField
          label={`* ${t('bos.pkdCode')}`}
          value={form.pkd_code}
          onChange={updateField('pkd_code')}
          fullWidth
          inputProps={{
            required: true,
            maxLength: 7,
            pattern: '\\d{2}\\.\\d{2}\\.[A-Z]',
          }}
          error={!!pkdCode && !isPkdCodeValid(pkdCode)}
          helperText={
            !!pkdCode && !isPkdCodeValid(pkdCode) && t('bos.pkdCodeError')
          }
          sx={{ mb: 3 }}
        />
        <TextField
          label={`* ${t('bos.email')}`}
          type="email"
          value={form.email}
          onChange={updateField('email')}
          fullWidth
          inputProps={{ required: true }}
          error={!!form.email && !isEmailValid(form.email)}
          helperText={
            !!form.email && !isEmailValid(form.email) && t('bos.emailError')
          }
          sx={{ mb: 3 }}
        />
        <TextField
          label={t('bos.phoneNumber')}
          value={form.phone_number}
          onChange={updateField('phone_number')}
          fullWidth
          inputProps={{ inputMode: 'numeric', maxLength: 15 }}
          sx={{ mb: 3 }}
        />
        <TextField
          label="Website"
          value={form.website}
          onChange={updateField('website')}
          name="website"
          sx={{ display: 'none' }}
          tabIndex={-1}
          autoComplete="off"
        />

        {CONSENT_SECTIONS.map((section, sectionIndex) => (
          <React.Fragment key={section.key}>
            <FormControl component="fieldset" sx={{ display: 'block', mb: 3 }}>
              <Typography component="legend" sx={{ mb: 1, fontWeight: 700 }}>
                {sectionIndex + 1}. {t(`bos.consents.${section.key}.title`)}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {section.consentKeys.map((key) => (
                  <FormControlLabel
                    key={key}
                    sx={{ alignItems: 'flex-start', m: 0 }}
                    control={
                      <Checkbox
                        checked={form.consents[key]}
                        onChange={updateConsent(key)}
                        disabled={
                          section.dependsOnKey
                            ? key !== section.dependsOnKey &&
                              !form.consents[section.dependsOnKey]
                            : false
                        }
                        sx={{ alignSelf: 'flex-start', pt: 0.25 }}
                      />
                    }
                    label={
                      <Typography sx={{ whiteSpace: 'pre-line' }}>
                        {isConsentRequired(key) && '* '}
                        {renderConsentLabel(
                          t(`bos.consents.${section.key}.${key}`)
                        )}
                      </Typography>
                    }
                  />
                ))}
                {section.footerKey && (
                  <Typography sx={{ ml: 5, whiteSpace: 'pre-line' }}>
                    {t(section.footerKey)}
                  </Typography>
                )}
                {isContactSectionMissingChannel(section) && (
                  <Typography color="error" variant="body2" sx={{ ml: 5 }}>
                    {t('bos.contactChannelRequired')}
                  </Typography>
                )}
              </Box>
            </FormControl>
            {section.key === 'ghg_calculation' && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('bos.dataProcessingNotice')}
              </Typography>
            )}
          </React.Fragment>
        ))}

        {mutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t('bos.error')}
          </Alert>
        )}

        <Button
          variant="contained"
          size="large"
          endIcon={<SendIcon />}
          disabled={!isComplete || mutation.isLoading}
          onClick={() => mutation.mutate()}
          sx={{ mt: 3 }}
        >
          {t('bos.submit')}
        </Button>
      </Box>
    </Box>
  );
}
